import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { startViewTransition } from '@theme/utilities';

/**
 * Collect all image URLs from a container (img src, img srcset, source srcset).
 * @param {Element} container - Root element (e.g. media-gallery)
 * @returns {string[]} Unique image URLs
 */
function collectImageUrls(container) {
  const urls = new Set();
  container.querySelectorAll('img[src]').forEach((img) => {
    const src = img.getAttribute('src');
    if (src) urls.add(src);
  });
  container.querySelectorAll('img[srcset], source[srcset]').forEach((el) => {
    const srcset = el.getAttribute('srcset');
    if (!srcset) return;
    srcset.split(',').forEach((part) => {
      const src = part.trim().split(/\s+/)[0];
      if (src) urls.add(src);
    });
  });
  return [...urls];
}

/**
 * Preload an image and resolve when loaded or after timeout.
 * @param {string} [src] - Image URL
 * @param {number} [timeoutMs] - Max wait ms
 * @returns {Promise<void>}
 */
function preloadImage(src, timeoutMs = 2500) {
  if (!src) return Promise.resolve();
  return new Promise((resolve) => {
    const img = new Image();
    const done = () => {
      clearTimeout(t);
      resolve();
    };
    img.onload = done;
    img.onerror = done;
    const t = setTimeout(done, timeoutMs);
    img.src = src;
  });
}

/**
 * Preload all images used in the gallery so they appear instantly after swap.
 * @param {Element} galleryElement - The new media-gallery DOM
 * @param {number} [timeoutMs] - Per-image timeout
 * @returns {Promise<void>}
 */
async function preloadAllGalleryImages(galleryElement, timeoutMs = 2500) {
  const urls = collectImageUrls(galleryElement);
  await Promise.all(urls.map((src) => preloadImage(src, timeoutMs)));
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   * Preloads all gallery images then uses a view transition so the gallery doesn't disappear.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const source = event.detail.data?.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    await preloadAllGalleryImages(newMediaGallery);

    await startViewTransition(() => {
      this.replaceWith(newMediaGallery);
    }, ['product-image-transition']);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
